import { SkillDto } from '@/api/generated';
import { SliderUi } from '@/components/form/Slider.ui.tsx';
import { useMobileMediaQuery } from '@/utils/useMobileMediaQuery.ts';
import React from 'react';

import { RatingScale } from './utils/ratingScales';

interface SkillRatingItemProps {
  disabled: boolean;
  onChange: (event: Event, value: number[] | number) => void;
  scale: RatingScale;
  skill: SkillDto;
  value: number;
}

const getLevelInfo = (value: number, scale: RatingScale) => {
  const label = scale.labels.find(l => l.value === value);
  const colorMap: Record<number, string> = {
    1: '#666',
    2: '#f16e00',
    3: '#f16e00',
    4: '#f16e00',
  };
  return {
    color: colorMap[value] ?? '#f16e00',
    label: label?.label ?? '',
  };
};

const SkillRatingItem: React.FC<SkillRatingItemProps> = props => {
  const { disabled, onChange, scale, skill, value } = props;
  const isMobile = useMobileMediaQuery();
  const marks = scale.labels.map(l => ({ value: l.value }));
  const levelInfo = getLevelInfo(value, scale);

  if (isMobile) {
    return (
      <div
        className="px-4 py-4 w-full transition-colors"
        style={{
          backgroundColor: disabled ? '#fafafa' : 'transparent',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <div className="flex items-center mb-3">
          <span className="font-bold text-sm flex-1" style={{ color: '#333' }}>
            {skill.description}
          </span>
        </div>
        <div className="relative">
          <div className="relative h-5 mb-1 px-3">
            {scale.labels.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === scale.labels.length - 1;
              const totalSteps = scale.labels.length - 1;
              const percentage = (index / totalSteps) * 100;

              return (
                <span
                  className="absolute top-0 font-medium whitespace-nowrap w-8"
                  key={item.value}
                  style={{
                    color: value === item.value ? levelInfo.color : '#666',
                    fontSize: '0.6rem',
                    fontWeight: value === item.value ? 700 : 500,
                    left: `${percentage}%`,
                    opacity: isFirst && value !== item.value ? 0.7 : 1,
                    transform: isFirst
                      ? 'translateX(30%)'
                      : isLast
                        ? 'translateX(-125%)'
                        : 'translateX(-40%)',
                    transition: 'color 0.2s, font-weight 0.2s',
                  }}
                >
                  {item.shortLabel}
                </span>
              );
            })}
          </div>
          <SliderUi
            disabled={disabled}
            marks={marks}
            max={scale.max}
            min={scale.min}
            name={skill.name}
            onChange={onChange}
            step={1}
            value={value}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-4 px-4 lg:px-6 py-4 w-full transition-all group"
      style={{
        backgroundColor: disabled ? '#fafafa' : 'transparent',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <div
        className="flex-shrink-0 font-bold text-sm group-hover:text-black transition-colors"
        style={{
          color: '#333',
          minWidth: '200px',
          width: '200px',
        }}
      >
        {skill.description}
      </div>
      <div className="flex-1" style={{ paddingRight: '0px' }}>
        <SliderUi
          disabled={disabled}
          marks={marks}
          max={scale.max}
          min={scale.min}
          name={skill.name}
          onChange={onChange}
          step={1}
          value={value}
        />
      </div>
    </div>
  );
};

export default SkillRatingItem;
